import { useState, useRef, useCallback, useEffect } from 'react';
import { isIOS, isMobile, isSafari } from 'react-device-detect';
import fixWebmDuration from 'fix-webm-duration';

export type RecordingState =
  | 'idle'
  | 'requesting-permission'
  | 'ready'
  | 'recording'
  | 'stopped'
  | 'error';

interface UseVideoRecorderOptions {
  maxDurationSeconds?: number;
  onRecordingComplete?: (blob: Blob) => void;
}

interface UseVideoRecorderReturn {
  state: RecordingState;
  error: string | null;
  recordedBlob: Blob | null;
  recordedUrl: string | null;
  elapsedSeconds: number;
  maxDurationSeconds: number;
  videoRef: React.RefObject<HTMLVideoElement>;
  isRecordingMode: boolean;
  startCamera: () => Promise<void>;
  startRecording: () => void;
  stopRecording: () => void;
  retake: () => void;
  cleanup: () => void;
  enterRecordingMode: () => void;
  exitRecordingMode: () => void;
}

const DEFAULT_MAX_DURATION = 30;

const DESKTOP_CAPTURE = { width: 1280, height: 720 } as const;
const MOBILE_CAPTURE = { width: 720, height: 720 } as const;

/**
 * Target encoded video bitrate. Caps how many bits MediaRecorder spends per
 * second of video (independent of resolution). Lower = smaller uploads / faster
 * loads; higher = sharper picture. ~2.5 Mbps ≈ 10MB for a 30s intro.
 */
const VIDEO_BITS_PER_SECOND = 1_500_000;
const AUDIO_BITS_PER_SECOND = 128_000;

function getCaptureSize(): { width: number; height: number } {
  return isMobile ? MOBILE_CAPTURE : DESKTOP_CAPTURE;
}

/**
 * WebM starts faster for progressive S3 playback, but iOS/Safari WebM
 * recording is rotated/stretched (WebKit). Prefer MP4 only on WebKit.
 */
function getPreferredMimeTypes(): string[] {
  const webmFirst = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
    'video/mp4',
  ];
  const mp4First = [
    'video/mp4',
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
  ];
  return isIOS || isSafari ? mp4First : webmFirst;
}

/**
 * Hook for recording video from webcam with a configurable time limit.
 * Uses native browser APIs (getUserMedia + MediaRecorder).
 */
export default function useVideoRecorder(
  options: UseVideoRecorderOptions = {}
): UseVideoRecorderReturn {
  const { maxDurationSeconds = DEFAULT_MAX_DURATION, onRecordingComplete } =
    options;

  const [state, setState] = useState<RecordingState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isRecordingMode, setIsRecordingMode] = useState(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  // Bumped on cleanup / retake / new start so a late async onstop is ignored.
  const recordingSessionRef = useRef(0);

  // Clean up object URLs when component unmounts or URL changes
  useEffect(() => {
    return () => {
      if (recordedUrl) {
        URL.revokeObjectURL(recordedUrl);
      }
    };
  }, [recordedUrl]);

  // Clean up stream and timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  // Attach stream to video element when both are available
  useEffect(() => {
    if (
      videoRef.current &&
      streamRef.current &&
      (state === 'ready' || state === 'recording')
    ) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {
        // Ignore play errors - browser may block autoplay
      });
    }
  }, [state]);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const cleanup = useCallback(() => {
    recordingSessionRef.current += 1;
    clearTimer();
    stopStream();
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current = null;
    }
    chunksRef.current = [];
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    setRecordedBlob(null);
    setRecordedUrl(null);
    setElapsedSeconds(0);
    setError(null);
    setState('idle');
    setIsRecordingMode(false);
  }, [clearTimer, stopStream, recordedUrl]);

  const enterRecordingMode = useCallback(() => {
    setIsRecordingMode(true);
  }, []);

  const exitRecordingMode = useCallback(() => {
    cleanup();
    setIsRecordingMode(false);
  }, [cleanup]);

  const startCamera = useCallback(async () => {
    try {
      setState('requesting-permission');
      setError(null);

      const { width, height } = getCaptureSize();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: width },
          height: { ideal: height },
        },
        audio: true,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        await videoRef.current.play();
      }

      setState('ready');
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to access camera';

      if (
        errorMessage.includes('Permission denied') ||
        errorMessage.includes('NotAllowedError')
      ) {
        setError(
          'Camera permission was denied. Please allow camera access and try again.'
        );
      } else if (errorMessage.includes('NotFoundError')) {
        setError('No camera found. Please connect a camera and try again.');
      } else {
        setError(`Failed to access camera: ${errorMessage}`);
      }
      setState('error');
    }
  }, []);

  const stopRecording = useCallback(() => {
    clearTimer();
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === 'recording'
    ) {
      mediaRecorderRef.current.stop();
    }
  }, [clearTimer]);

  const startRecording = useCallback(() => {
    if (!streamRef.current) {
      setError('Camera not started');
      return;
    }

    // eslint-disable-next-line no-plusplus
    const sessionId = ++recordingSessionRef.current;
    chunksRef.current = [];
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    setRecordedBlob(null);
    setRecordedUrl(null);
    setElapsedSeconds(0);

    // Prefer WebM (faster progressive start) except on iOS/Safari (orientation bug).
    const supportedMimeType = getPreferredMimeTypes().find((type) =>
      MediaRecorder.isTypeSupported(type)
    );

    if (!supportedMimeType) {
      setError('No supported video format found in this browser');
      setState('error');
      return;
    }

    try {
      const mediaRecorder = new MediaRecorder(streamRef.current, {
        mimeType: supportedMimeType,
        videoBitsPerSecond: VIDEO_BITS_PER_SECOND,
        audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
      });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // Snapshot chunks before any await — cleanup/retake may clear chunksRef.
        const chunks = chunksRef.current.slice();
        let blob = new Blob(chunks, { type: supportedMimeType });
        if (supportedMimeType.startsWith('video/webm')) {
          const durationMs = Date.now() - startTimeRef.current;
          try {
            blob = await fixWebmDuration(blob, durationMs, { logger: false });
          } catch {
            // Keep the raw blob — better than leaving UI/stream stuck in 'recording'.
          }
        }
        // Drop results if cleanup, retake, or a newer startRecording ran meantime.
        if (sessionId !== recordingSessionRef.current) {
          return;
        }
        const url = URL.createObjectURL(blob);
        setRecordedBlob(blob);
        setRecordedUrl(url);
        setState('stopped');
        stopStream();
        onRecordingComplete?.(blob);
      };

      mediaRecorder.onerror = () => {
        setError('Recording failed unexpectedly');
        setState('error');
        clearTimer();
      };

      mediaRecorder.start(1000); // Collect data every second for smoother progress
      startTimeRef.current = Date.now();
      setState('recording');

      // Start timer for elapsed time display and auto-stop at max duration
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setElapsedSeconds(elapsed);

        if (elapsed >= maxDurationSeconds) {
          stopRecording();
        }
      }, 100);
    } catch (err) {
      setError('Failed to start recording');
      setState('error');
    }
  }, [
    maxDurationSeconds,
    onRecordingComplete,
    recordedUrl,
    stopRecording,
    clearTimer,
    stopStream,
  ]);

  const retake = useCallback(() => {
    recordingSessionRef.current += 1;
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    setRecordedBlob(null);
    setRecordedUrl(null);
    setElapsedSeconds(0);
    setError(null);
    chunksRef.current = [];
    setState('idle');
  }, [recordedUrl]);

  return {
    state,
    error,
    recordedBlob,
    recordedUrl,
    elapsedSeconds,
    maxDurationSeconds,
    videoRef,
    isRecordingMode,
    startCamera,
    startRecording,
    stopRecording,
    retake,
    cleanup,
    enterRecordingMode,
    exitRecordingMode,
  };
}
